import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  subtitle?: string;
  description?: string;
  badge?: string;
  badgeColor?: 'main' | 'active' | 'success' | 'warning';
  balance?: string;
  usdValue?: string;
}

interface GlobalDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  dropdownClassName?: string;
  disabled?: boolean;
  showSearch?: boolean;
}

const GlobalDropdown: React.FC<GlobalDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select option',
  className = '',
  dropdownClassName = '',
  disabled = false,
  showSearch = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside or when cleanup event is triggered
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both the button and the dropdown content
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideDropdown = !(target as Element).closest?.('[class*="bg-[#19191e]"]');
      
      if (isOutsideButton && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleCloseAllDropdowns = () => {
      setIsOpen(false);
    };

    const handleForceCloseDropdowns = () => {
      setIsOpen(false);
      setSearchTerm('');
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    window.addEventListener('ekko:close_all_dropdowns', handleCloseAllDropdowns);
    window.addEventListener('ekko:force_close_dropdowns', handleForceCloseDropdowns);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('ekko:close_all_dropdowns', handleCloseAllDropdowns);
      window.removeEventListener('ekko:force_close_dropdowns', handleForceCloseDropdowns);
    };
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = showSearch && searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const selectedOption = options.find(opt => opt.value === value);

  const getBadgeStyles = (badgeColor: string) => {
    switch (badgeColor) {
      case 'main': return 'bg-blue-50 text-blue-600 border border-blue-200';
      case 'active': return 'bg-[#24c35b]/10 text-[#24c35b] border border-[#24c35b]/20';
      case 'success': return 'bg-green-50 text-green-600 border border-green-200';
      case 'warning': return 'bg-yellow-50 text-yellow-600 border border-yellow-200';
      default: return 'bg-gray-50 text-gray-600 border border-gray-200';
    }
  };

  return (
    <div className={`relative global-dropdown ${className}`} ref={dropdownRef}>
      <button
        onClick={() => {
          if (!disabled) {
            if (!isOpen && dropdownRef.current) {
              const rect = dropdownRef.current.getBoundingClientRect();
              setDropdownPosition({
                left: rect.left,
                top: rect.bottom + 4,
                width: Math.max(rect.width, 256)
              });
            }
            setIsOpen(!isOpen);
          }
        }}
        disabled={disabled}
        className={`flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-gray-200 text-black text-sm hover:border-[#24c35b] focus:border-[#24c35b] transition-all w-full justify-between ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span className="truncate">
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown 
          size={12} 
          className={`transition-transform flex-shrink-0 text-gray-500 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && createPortal(
        <div 
          className={`bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto custom-scrollbar ${dropdownClassName}`} 
          style={{ 
            position: 'fixed',
            zIndex: 999999,
            left: dropdownPosition.left,
            top: dropdownPosition.top,
            width: dropdownPosition.width,
            minWidth: '256px',
            maxHeight: '200px', // Smaller max height
            height: 'auto' // Let it size to content but respect maxHeight
          }}
        >
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b]"
              />
            </div>
          )}
          
          {filteredOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm('');
              }}
              className={`w-full text-left px-3 py-3 text-sm hover:bg-gray-50 transition-all border-b border-gray-100 last:border-b-0 ${
                value === option.value ? 'bg-[#24c35b]/10 text-[#24c35b] border-[#24c35b]/20' : 'text-black'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{option.label}</div>
                    {option.description && (
                      <div className="text-gray-500 text-xs ml-2">
                        {option.description}
                      </div>
                    )}
                    {option.badge && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getBadgeStyles(option.badgeColor || 'main')}`}>
                        {option.badge}
                      </span>
                    )}
                  </div>
                  {option.subtitle && (
                    <div className="text-gray-500 font-mono text-xs mt-1">
                      {option.subtitle}
                    </div>
                  )}
                </div>
                
                {(option.balance || option.usdValue) && (
                  <div className="text-right ml-2">
                    {option.balance && (
                      <div className="text-black font-mono text-sm font-medium">
                        {option.balance}
                      </div>
                    )}
                    {option.usdValue && (
                      <div className="text-gray-500 text-xs">
                        {option.usdValue}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
          
          {filteredOptions.length === 0 && (
            <div className="px-3 py-4 text-gray-500 text-sm text-center">
              No options found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default GlobalDropdown; 